import React, { useMemo } from 'react'
import { Modal, Row, Col, Button } from 'react-bootstrap'
import {
  Box,
  Grid,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableFooter,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import dayjs from 'dayjs'

import { useTimeFormat } from 'contexts/TimeFormatContext'

const CompareModal = ({ show, onHide, data = [] }) => {
  const { formatDate } = useTimeFormat()
  const leftWidth = 0
  const fieldColWidth = 60
  const itemColWidth = 120

  const outputFields = useMemo(() => {
    return Array.from(
      new Set(data.flatMap((d) => Object.keys(d.outputJson || {}))),
    )
  }, [data])

  const DATE_FIELDS = new Set(['DOB', 'DateDsc'])

  const formatValue = (field, value) => {
    if (value === null || value === undefined) return '-'

    if (DATE_FIELDS.has(field)) {
      return value ? formatDate(value) : '-'
    }

    if (typeof value === 'object') {
      return JSON.stringify(value)
    }

    return String(value)
  }

  const tableMinWidth = fieldColWidth + data.length * itemColWidth
  const MODAL_WIDTH = window.innerWidth * 0.9
  const shouldScrollX = tableMinWidth > MODAL_WIDTH

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName="modal-90w"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          Compare Revision
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          <TableContainer
            component={Paper}
            sx={{
              width: '100%',
              maxWidth: '100%',
              maxHeight: '75vh',
              overflowX: shouldScrollX ? 'auto' : 'hidden',
              overflowY: 'auto',
            }}
          >
            <Table
              size="small"
              sx={{
                minWidth: tableMinWidth,
                tableLayout: 'fixed',
                borderCollapse: 'separate',
                width: shouldScrollX ? 'max-content' : '100%',
              }}
            >
              <colgroup>
                <col
                  style={{
                    width: `${fieldColWidth}px`,
                    minWidth: `${fieldColWidth}px`,
                    maxWidth: `${fieldColWidth}px`,
                  }}
                />
                {data.map((item) => (
                  <col
                    key={item.inPatientDrgRevisionId}
                    style={{
                      width: `${itemColWidth}px`,
                      minWidth: `${itemColWidth}px`,
                    }}
                  />
                ))}
              </colgroup>

              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: leftWidth,
                      top: 0,
                      zIndex: 6,
                      backgroundColor: '#f5f5f5',
                      fontWeight: 'bold',
                      borderRight: 1,
                      borderLeft: 1,
                      borderTop: 1,
                      borderColor: 'divider',
                      whiteSpace: 'nowrap',
                      width: fieldColWidth,
                      minWidth: fieldColWidth,
                      maxWidth: fieldColWidth,
                    }}
                  ></TableCell>

                  {data.map((item) => {
                    const dateStr = dayjs(item.createdAt)
                      .add(543, 'y')
                      .format('DD/MM/YYYY')

                    return (
                      <TableCell
                        key={item.inPatientDrgRevisionId}
                        align="center"
                        sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 6,
                          padding: '6px 4px',
                          fontSize: '0.875rem',
                          borderRight: 1,
                          borderTop: 1,
                          borderColor: 'divider',
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                        }}
                      >
                        {dateStr}
                      </TableCell>
                    )
                  })}
                </TableRow>
              </TableHead>

              <TableBody>
                {outputFields.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={data.length + 1}
                      align="center"
                      sx={{ py: 3, color: 'text.secondary' }}
                    >
                      No output data
                    </TableCell>
                  </TableRow>
                )}

                {outputFields.map((field) => (
                  <TableRow key={field}>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: leftWidth,
                        zIndex: 4,
                        backgroundColor: '#fff',
                        fontWeight: 500,
                        borderRight: 1,
                        borderLeft: 1,
                        borderColor: 'divider',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {field}
                    </TableCell>

                    {data.map((item) => {
                      const value = item.outputJson?.[field]

                      return (
                        <TableCell
                          key={`${field}-${item.inPatientDrgRevisionId}`}
                          align="center"
                          sx={{
                            fontSize: '0.875rem',
                            borderRight: 1,
                            borderColor: 'divider',
                          }}
                        >
                          {formatValue(field, value)}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          <i className="feather icon-x my-auto" /> Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
export default CompareModal
